var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { useMemo, useState, useCallback, useDebugValue } from "react";
import { EMPTY_BREAKPOINT } from "./types";
import { createMediaQueries } from "./create-media-queries";
import { useIsomorphicEffect } from "../use-isomorpic-effect/use-isomorpic-effect";
/**
 * A React hook to use the current responsive breakpoint.
 * Will listen to changes using the window.matchMedia API.
 * @param {*} config the list of configured breakpoint names and their pixel values
 * @param {*} [defaultBreakpoint] the optional default breakpoint
 * @param {*} [hydrateInitial] whether to return the default breakpoint on first render. Set to `false` if the real breakpoint should be returned instead. Only applies to the browser, not server-side.
 *
 * @example
 * const breakpoints = { mobile: 0, tablet: 768, desktop: 1280 }
 * ...
 * const result = useBreakpoint(breakpoints)
 * // { breakpoint: string; minWidth: number; maxWidth: number | null } | { breakpoint: undefined; minWidth: undefined; maxWidth: undefined }
 *
 * @example <caption>With default value</caption>
 * const breakpoints = { mobile: 0, tablet: 768, desktop: 1280 }
 * ...
 * const result = useBreakpoint(breakpoints, 'mobile')
 * // breakpoint: { breakpoint: string; minWidth: number; maxWidth: number | null }
 *
 * @example <caption>With default value, but not hydrated. This means the breakpoint might be different on the initial render.</caption>
 * const breakpoints = { mobile: 0, tablet: 768, desktop: 1280 }
 * ...
 * const result = useBreakpoint(breakpoints, 'mobile', false)
 * // breakpoint: { breakpoint: string; minWidth: number; maxWidth: number | null }
 */
var useBreakpoint = function (config, defaultBreakpoint, hydrateInitial) {
    if (hydrateInitial === void 0) { hydrateInitial = true; }
    /** Memoize list of calculated media queries from config */
    var mediaQueries = useMemo(function () { return createMediaQueries(config); }, [config]);
    /** Get initial breakpoint value */
    var _a = __read(useState(function () {
        var e_1, _a;
        try {
            /** Loop through all media queries */
            for (var mediaQueries_1 = __values(mediaQueries), mediaQueries_1_1 = mediaQueries_1.next(); !mediaQueries_1_1.done; mediaQueries_1_1 = mediaQueries_1.next()) {
                var _b = mediaQueries_1_1.value;
                var query = _b.query, breakpoint = __rest(_b, ["query"]);
                /**
                 * If we're in the browser and there's no default value,
                 * try to match actual breakpoint. If the default value
                 * should not be hydrated, use the actual breakpoint.
                 */
                if (typeof window !== "undefined" &&
                    !(defaultBreakpoint && hydrateInitial)) {
                    var mediaQuery = window.matchMedia(query);
                    if (mediaQuery.matches) {
                        return breakpoint;
                    }
                }
                else if (breakpoint.breakpoint === defaultBreakpoint) {
                    /** Otherwise, try to match default value */
                    return breakpoint;
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (mediaQueries_1_1 && !mediaQueries_1_1.done && (_a = mediaQueries_1.return)) _a.call(mediaQueries_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        return EMPTY_BREAKPOINT;
    }), 2), currentBreakpoint = _a[0], setCurrentBreakpoint = _a[1];
    /** If there's a match, update the current breakpoint */
    var updateBreakpoint = useCallback(function (_a, breakpoint) {
        var matches = _a.matches;
        if (matches) {
            setCurrentBreakpoint(breakpoint);
        }
    }, []);
    /** On changes to mediaQueries, subscribe to changes using window.matchMedia */
    useIsomorphicEffect(function () {
        var unsubscribers = mediaQueries.map(function (_a) {
            var query = _a.query, breakpoint = __rest(_a, ["query"]);
            var list = window.matchMedia(query);
            updateBreakpoint(list, breakpoint);
            var handleChange = function (event) {
                updateBreakpoint(event, breakpoint);
            };
            list.addListener(handleChange);
            /** Map the unsubscribers array to a list of unsubscriber methods */
            return function () { return list.removeListener(handleChange); };
        });
        /** Return a function that when called, will call all unsubscribers */
        return function () { return unsubscribers.forEach(function (unsubscriber) { return unsubscriber(); }); };
    }, [mediaQueries, updateBreakpoint]);
    /** Print a nice debug value for React Devtools */
    useDebugValue(currentBreakpoint, function (c) {
        return typeof c.breakpoint === "string"
            ? c.breakpoint + " (" + c.minWidth + " \u2264 x" + (c.maxWidth ? " < " + (c.maxWidth + 1) : "") + ")"
            : "";
    });
    return currentBreakpoint;
};
export default useBreakpoint;
